/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { shallowRef } from "vue";
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import session from '../../../../../share/session';
import config from '../../../../../share/config';
import inputValidator from '../../../../../share/util/inputValidator';
import requirement004 from '../../../../../components/vue/individualWorkSupport/Requirement004.vue';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  INPUT: 'input',
  ERROR: 'error',
  FAILED: 'failed',
  SUCCESS: 'success'
};

export default {
  name: 'requirementSpECM003',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      isDisabled: false,
      displayInfoData: '', // 暗証番号
      inputData: '', // 入力値
      isAlert: {}, // アラート情報
      displayInfoVin: '', // VIN
      // 以下、WS4への画面遷移用パラメータ
      currentWizard: '',
      ws4i18nWsKey: '',
      ws4workSupportId: '',
      isTransition: false,
      modal_special_requirement_ECM3: false
    };
  },

  props: {
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleInfo: state => state.cache.vehicleInfo
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.START:
        case PHASE.INPUT:
          return 'secondary';
        case PHASE.ERROR:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        default:
          return 'secondary';
      }
    }
  },

  mounted() {
    // 初期化処理を呼び出す
    this.init();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    async init() {
      this.modal_special_requirement_ECM3 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    //----- htmlイベント処理 -----------------------------------------------//

    /** 暗証番号入力表示画面キャンセルボタンクリックイベント */
    async clickClose() {
      // 画面を閉じる
      this.$emit('close-wizard');
      this.modal_special_requirement_ECM3 = false;
    },

    /** 暗証番号入力表示画面OKボタンクリックイベント */
    async clickComparePostGet() {
      // 暗証番号比較処理を呼び出す
      await this.comparePostGet();
    },

    /** 暗証番号入力表示画面入力ボタンクリックイベント */
    clickInput() {
      // アラート情報をクリア
      this.clearInputAlert();
      // 暗証番号入力画面を表示
      this.showInput();
    },

    /**
     * 暗証番号入力画面キャンセルボタンクリックイベント
     * 暗証番号入力エラー画面OKボタンクリックイベント
     */
    clickStart() {
      // 暗証番号入力画面に表示用暗証番号を反映
      this.inputData = this.displayInfoData;
      // アラート情報をクリア
      this.clearInputAlert();
      // 暗証番号入力表示画面を表示
      this.showStart();
    },

    /** 暗証番号入力画面OKボタンクリックイベント */
    clickInputStart() {
      // バリデーションチェック
      if (!this.validate()) {
        // エラーの場合、以降の処理を行わない
        return;
      }
      // 暗証番号入力表示画面に暗証番号を反映
      this.displayInfoData = this.inputData;
      // アラート情報をクリア
      this.clearInputAlert();
      // 暗証番号入力表示画面を表示
      this.showStart();
    },

    /** 暗証番号入力画面クリアボタンクリックイベント */
    clickClear() {
      // 入力された暗証番号をクリア
      this.inputData = '';
      // アラート情報をクリア
      this.clearInputAlert();
    },

    /** VIN登録確認表示画面YESボタンクリックイベント */
    async clickExitPostGet() {
      // 終了処理を呼び出す
      await this.exitPostGet();
      // 終了処理の結果に関わらず、画面を閉じる
      this.clickClose();
    },

    /** VIN登録確認表示画面NOボタンクリックイベント */
    clickTransition() {
      // WS.4 [再書込画面]に遷移する
      this.isTransition = true;
      this.ws4workSupportId = 401;
      this.ws4i18nWsKey = `ws${this.systemId}.${this.ws4workSupportId}`;
      this.currentWizard = shallowRef(requirement004);
      // ECM3モーダルを閉じる
      this.modal_special_requirement_ECM3 = false;
    },

    /** 入力画面アラートクリアイベント */
    clearInputAlert() {
      // アラート情報をクリア
      this.isAlert = {
        data: { valid: null, msg: '' }
      };
    },

    /** バリデーションチェック */
    validate() {
      this.isAlert.data = inputValidator.validateCommons(
        this.inputData, false, 4, /^[0-9]*$/
      );
      return this.isAlert.data.valid === null;
    },

    //----- PHASE操作処理 -----------------------------------------------//

    /** 暗証番号入力表示処理 */
    showStart() {
      this.phase = PHASE.START;
    },

    /** 暗証番号入力表示処理 */
    showInput() {
      this.phase = PHASE.INPUT;
    },

    /** 暗証番号入力エラー表示処理 */
    showError() {
      this.phase = PHASE.ERROR;
    },

    /** VIN登録確認表示処理 */
    showSuccess() {
      this.phase = PHASE.SUCCESS;
    },

    /** 通信エラー表示処理 */
    showFailed() {
      this.phase = PHASE.FAILED;
    },

    //----- バックエンドAPI実行処理 -----------------------------------------------//

    /**
     * 暗証番号比較処理
     */
    async comparePostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'compare',
            pin_number: this.displayInfoData
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // 暗証番号比較結果で分岐
            if (response.data.user_data.result === 'ok') {
              // [暗証番号の比較結果: 一致]
              // VINをVIN登録確認表示画面に設定
              this.displayInfoVin = this.vehicleInfo.vehicle.vin.vin;
              // VIN登録確認表示画面に遷移
              this.showSuccess();
            } else {
              // [暗証番号の比較結果: 不一致]
              // 暗証番号入力エラー表示処理画面に遷移
              this.showError();
            }
          } else {
            // 通信エラー画面に遷移
            this.showUnexpectedError();
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * 終了処理
     */
    async exitPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            is_special: true,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * API 通信で通信バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 通信エラー画面に遷移
      this.showFailed();
    },

  }
};